import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { ChefHat, Camera, LinkSimple, Trash, X, ImageSquare } from "@phosphor-icons/react";
import { SafeRecipeImage } from "./SafeRecipeImage";

/**
 * Recipe cover editor.
 * - Shows the current cover (or placeholder).
 * - On hover/click reveals an overlay with: Subir foto / Pegar URL / Quitar.
 * - Lifts updates back via `onUpdate(recipe)`.
 */
export default function CoverEditor({ recipe, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [imageFailed, setImageFailed] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setError("Sube un archivo de imagen (jpg, png, webp…).");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/recipes/${recipe.id}/cover/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdate(data);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo subir la imagen");
    } finally {
      setBusy(false);
    }
  };

  const submitUrl = async () => {
    if (!urlValue.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/recipes/${recipe.id}/cover/url`, {
        image_url: urlValue.trim(),
      });
      onUpdate(data);
      setShowUrlInput(false);
      setUrlValue("");
    } catch (e) {
      setError(e.response?.data?.detail || "URL no válida");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm("¿Quitar la portada de esta receta?")) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.delete(`/recipes/${recipe.id}/cover`);
      onUpdate(data);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo quitar la portada");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => setImageFailed(false), [recipe.image_url]);

  const hasImage = Boolean(recipe.image_url) && !imageFailed;

  return (
    <div
      className="relative rounded-3xl overflow-hidden aspect-[16/10] bg-[hsl(var(--muted))] group"
      data-testid="cover-editor"
    >
      {hasImage ? (
        <SafeRecipeImage
          src={recipe.image_url}
          alt={recipe.title}
          className="absolute inset-0 w-full h-full object-cover"
          testId="cover-editor-image"
          onFailure={() => setImageFailed(true)}
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-[hsl(var(--muted-foreground))] gap-2">
          <ChefHat size={64} weight="thin" />
          <span className="text-xs">Sin portada</span>
        </div>
      )}

      {/* Overlay actions */}
      {!showUrlInput && (
        <div
          className={`absolute inset-0 flex items-end p-3 sm:p-4 ${hasImage ? "bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-0 group-hover:opacity-100" : "opacity-100"} transition-opacity`}
        >
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="cover-upload-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/95 text-[hsl(var(--foreground))] text-xs font-semibold btn-press shadow disabled:opacity-50"
            >
              <ImageSquare size={14} weight="fill" /> {hasImage ? "Cambiar foto" : "Subir foto"}
            </button>
            <button
              onClick={() => cameraRef.current?.click()}
              disabled={busy}
              data-testid="cover-camera-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/95 text-[hsl(var(--foreground))] text-xs font-semibold btn-press shadow disabled:opacity-50 sm:hidden"
            >
              <Camera size={14} weight="fill" /> Tomar foto
            </button>
            <button
              onClick={() => { setShowUrlInput(true); setError(""); }}
              disabled={busy}
              data-testid="cover-url-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/95 text-[hsl(var(--foreground))] text-xs font-semibold btn-press shadow disabled:opacity-50"
            >
              <LinkSimple size={14} weight="bold" /> Pegar URL
            </button>
            {hasImage && (
              <button
                onClick={remove}
                disabled={busy}
                data-testid="cover-remove-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/95 text-[hsl(var(--destructive))] text-xs font-semibold btn-press shadow disabled:opacity-50"
              >
                <Trash size={14} weight="bold" /> Quitar
              </button>
            )}
          </div>
        </div>
      )}

      {/* URL input panel */}
      {showUrlInput && (
        <div className="absolute inset-0 bg-[hsl(var(--foreground))]/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl p-4 shadow-2xl">
            <div className="flex items-center justify-between mb-2">
              <p className="font-serif text-lg">Pegar URL de imagen</p>
              <button
                onClick={() => { setShowUrlInput(false); setUrlValue(""); }}
                className="text-[hsl(var(--muted-foreground))]"
                data-testid="cover-url-close"
              >
                <X size={18} />
              </button>
            </div>
            <input
              autoFocus
              type="url"
              placeholder="https://..."
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitUrl(); }}
              data-testid="cover-url-input"
              className="w-full px-4 py-2.5 bg-[hsl(var(--background))] rounded-full border border-[hsl(var(--border))] text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => { setShowUrlInput(false); setUrlValue(""); }}
                className="px-3 py-1.5 rounded-full text-xs text-[hsl(var(--muted-foreground))]"
              >
                Cancelar
              </button>
              <button
                onClick={submitUrl}
                disabled={busy || !urlValue.trim()}
                data-testid="cover-url-submit"
                className="px-4 py-1.5 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-semibold btn-press disabled:opacity-50"
              >
                {busy ? "Guardando…" : "Guardar"}
              </button>
            </div>
            {error && <p className="text-xs text-[hsl(var(--destructive))] mt-2">{error}</p>}
          </div>
        </div>
      )}

      {/* File inputs (hidden) */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="cover-file-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="cover-camera-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {error && !showUrlInput && (
        <div className="absolute top-2 left-2 right-2 text-xs text-white bg-[hsl(var(--destructive))]/90 rounded-full px-3 py-1.5 text-center">
          {error}
        </div>
      )}
      {busy && (
        <div className="absolute top-2 right-2 text-[10px] text-white bg-black/60 rounded-full px-2 py-0.5">
          Guardando…
        </div>
      )}
    </div>
  );
}
